import type { SiteDocument, SiteNode } from "@zirro/schema";
import type { PageLayoutOption } from "@zirro/editor";
import type { TemplateVariation } from "./api";

/** Business details a layout's {{business.*}} tokens are filled with. */
export interface BusinessValues {
  name: string;
  email: string;
  phone: string;
  description: string;
}

/** Collects every string in a document (to find contact details). */
function strings(value: unknown, out: string[] = []): string[] {
  if (typeof value === "string") out.push(value);
  else if (Array.isArray(value)) value.forEach((v) => strings(v, out));
  else if (value && typeof value === "object") Object.values(value).forEach((v) => strings(v, out));
  return out;
}

/**
 * Reads the site's own business details back out of its document: the
 * header name, the first email/phone link, and the meta description.
 */
export function businessValuesFrom(doc: SiteDocument, fallbackName: string): BusinessValues {
  const all = strings(doc);
  const email = all.find((s) => s.startsWith("mailto:"))?.slice("mailto:".length) ?? "";
  const phone = all.find((s) => s.startsWith("tel:"))?.slice("tel:".length) ?? "";
  const headerName = (doc.header?.props as { logoText?: unknown } | undefined)?.logoText;
  return {
    name: typeof headerName === "string" && headerName.trim() ? headerName : fallbackName,
    email,
    phone,
    description: doc.meta?.description ?? "",
  };
}

type Json = unknown;

/**
 * Fills {{business.*}} tokens and repairs links so a layout always yields a
 * valid page: email buttons without an email point to the Contact page, and
 * links to pages the owner deleted point home.
 */
export function prepareLayoutNodes(nodes: SiteNode[], doc: SiteDocument, values: BusinessValues): SiteNode[] {
  const pageIds = new Set(doc.pages.map((p) => p.id));
  const home = doc.pages.find((p) => p.slug === "/") ?? doc.pages[0];
  const contact = doc.pages.find((p) => p.slug === "/contact");
  const fill = (s: string) =>
    s
      .replaceAll("{{business.name}}", values.name)
      .replaceAll("{{business.email}}", values.email)
      .replaceAll("{{business.phone}}", values.phone)
      .replaceAll("{{business.description}}", values.description);

  const fixHref = (href: Record<string, Json>): Record<string, Json> | null => {
    if (href.type === "page") {
      return pageIds.has(String(href.pageId)) ? href : home ? { type: "page", pageId: home.id } : null;
    }
    if (href.type === "url" && typeof href.url === "string") {
      const url = fill(href.url);
      if (url === "mailto:" || url === "tel:") {
        return contact ? { type: "page", pageId: contact.id } : null;
      }
      return { ...href, url };
    }
    return href;
  };

  const walk = (value: Json, key?: string): Json => {
    if (typeof value === "string") return fill(value);
    if (Array.isArray(value)) return value.map((v) => walk(v));
    if (value && typeof value === "object") {
      const obj = value as Record<string, Json>;
      if (key === "href" && "type" in obj) return fixHref(obj);
      const out: Record<string, Json> = {};
      for (const [k, v] of Object.entries(obj)) out[k] = walk(v, k);
      return out;
    }
    return value;
  };

  // Buttons whose link could not be repaired are dropped.
  const prune = (list: SiteNode[]): SiteNode[] =>
    list
      .filter((n) => !(n.type === "Button" && (n.props as { href?: unknown }).href === null))
      .map((n) => (n.children ? { ...n, children: prune(n.children) } : n));

  return prune(nodes.map((n) => walk(n) as SiteNode));
}

/** Template variations as editor layout options for this site. */
export function layoutsForSite(
  variations: TemplateVariation[] | undefined,
  doc: SiteDocument | undefined,
  siteName: string,
): PageLayoutOption[] {
  if (!variations || !doc) return [];
  const values = businessValuesFrom(doc, siteName);
  const slugs = new Set(doc.pages.map((p) => p.slug));
  return variations
    .filter((v) => slugs.has(v.page))
    .map((v) => ({
      id: v.id,
      pageSlug: v.page,
      name: v.name,
      description: v.description,
      nodes: prepareLayoutNodes(v.nodes as unknown as SiteNode[], doc, values),
    }));
}
